
import AppLayout from "@/components/layout/AppLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { TeamList } from "./components/TeamList";
import { ReportsHeader } from "./components/ReportsHeader";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { getUserTeamIds } from "@/pages/guards/services/guardFetchService";
import { useUserRoles } from "@/hooks/useUserRoles";

const Reports = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { isAdmin } = useUserRoles();

  // SEO: set page title, description, and canonical link
  useEffect(() => {
    document.title = "Reports & Submissions | Guardly";
    const desc = "Manage team reports, submissions, and emergency data.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = desc;

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = window.location.href;
  }, []);

  const { data: teams, isLoading, error } = useQuery({
    queryKey: ['teams-with-reports', user?.id, isAdmin],
    queryFn: async () => {
      if (!user) return [];

      console.log(`Fetching teams with reports for user ${user.id}, isAdmin: ${isAdmin}`);

      // For admin users, show all teams
      if (isAdmin) {
        console.log('Admin user - fetching all teams with reports');
        const { data: teams, error } = await supabase
          .from('teams')
          .select(`
            *,
            reports:reports(count)
          `)
          .eq('status', 'active')
          .order('name');

        if (error) {
          console.error('Error loading teams for admin:', error);
          toast({
            title: "Error loading teams",
            description: error.message,
            variant: "destructive",
          });
          return [];
        }

        console.log(`Admin teams with reports fetched (${teams?.length || 0} teams):`, teams);
        return teams;
      } 
      
      // For non-admin users, only show teams they have access to
      console.log('Regular user - fetching assigned teams with reports');
      const userTeamIds = await getUserTeamIds(user.id);
      console.log('User team IDs for reports:', userTeamIds);
      
      if (userTeamIds.length === 0) {
        console.log('No teams found for this user in reports');
        return [];
      }
      
      const { data: teams, error } = await supabase
        .from('teams')
        .select(`
          *,
          reports:reports(count)
        `)
        .in('id', userTeamIds)
        .eq('status', 'active')
        .order('name');

      if (error) {
        console.error('Error loading teams for user:', error);
        toast({
          title: "Error loading teams",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }

      console.log(`User teams with reports fetched (${teams?.length || 0} teams):`, teams);
      return teams;
    },
    enabled: !!user,
  });

  const handleAddReport = () => {
    if (teams && teams.length > 0) {
      navigate(`/reports/team/${teams[0].id}`);
    } else {
      toast({
        title: "No teams available",
        description: "Please create a team first",
        variant: "destructive",
      });
    }
  };

  if (error) {
    return (
      <AppLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6">
              <p className="text-destructive">Error loading teams data. Please try again.</p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <main className="relative px-4 sm:px-6 md:px-8 lg:px-10 py-6 sm:py-8 animate-fadeIn" role="main" aria-label="Reports and submissions">
        <section className="space-y-8">
          <ReportsHeader onReportsAdded={handleAddReport} />
          <TeamList teams={teams} isLoading={isLoading} />
        </section>
      </main>
    </AppLayout>
  );
};

export default Reports;
